import React, { useEffect, useState } from "react";
import withStyles from "@mui/styles/withStyles";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";

const styles = (theme) => ({
  post: {
    border: "2px solid " + theme.palette.primary.main + "80",
    borderRadius: "15px",
    padding: theme.spacing(4),
    maxWidth: "1000px",
    margin: "auto",
    cursor: "pointer",
    transition: "0.25s",
    width: "100%",
    color: theme.palette.common.contrast,
    background: theme.palette.common.white,
    "&:hover": {
      scale: "1.02",
      backgroundColor: theme.palette.primary.main + "16", // 33 is the hex code for 20% opacity
      borderColor: theme.palette.primary.main,
    },
  },

  profileContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),

    "& svg": {
      fill: theme.palette.common.contrast,
    },

    "& img": {
      width: "55px",
      height: "55px",
      borderRadius: "50%",
      objectFit: "cover",
    },

    "& .postTime": {
      color: theme.palette.common.text,
    },
  },

  postContainer: {
    "& p": {
      margin: 0,
    },
  },

  postTextOverview: {
    display: "-webkit-box",
    WebkitLineClamp: 5,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    color:
      theme.palette.mode === "light"
        ? "#64748B"
        : theme.palette.common.contrast,
  },

  postImg: {
    height: "220px",
    width: "100%",
    aspectRatio: "16/9",
    objectFit: "cover",
    borderRadius: "8px",
    margin: theme.spacing(2, 0),
  },

  likesContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: theme.palette.common.textAlt,
    "& div": {
      display: "flex",
      gap: theme.spacing(1),
      alignItems: "center",
    },
  },

  line: {
    width: "100%",
    background:
      theme.palette.mode === "light" ? "#ccc" : theme.palette.common.contrast,
    height: "1px",
    borderRadius: "5px",
    margin: theme.spacing(2, 0),
  },

  optionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    "& div": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    "& path": {
      stroke: theme.palette.common.contrast,
    },
  },

  likedByUser: {
    color: theme.palette.primary.main,
    "& path": {
      stroke: theme.palette.primary.main,
    },
  },
});

const PostOverView = ({ classes, post }) => {
  const history = useHistory();
  const [likedByUser, setLikedByUser] = React.useState(post.userHasLiked);

  const handlePostClick = () => {
    history.push({
      pathname: `/community/post/${post.slug}`,
      state: { post: post },
    });
  };

  function calculateTimeAgo() {
    let date = new Date(post.date);
    let now = new Date();

    let diffInMs = now - date;

    let diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    let diffInMonths =
      (now.getFullYear() - date.getFullYear()) * 12 +
      (now.getMonth() - date.getMonth());

    return {
      days: diffInDays,
      months: diffInMonths,
    };
  }

  let timeAgo = calculateTimeAgo();

  return (
    <div className={classes.post} onClick={() => handlePostClick()}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Box className={classes.profileContainer}>
          <div>
            {post.user?.image ? (
              <img src={post.user.image} alt="" />
            ) : (
              <svg
                version="1.1"
                id="Vrstva_1"
                x="0px"
                y="0px"
                viewBox="0 0 100 100"
                width={55}
                height={55}
              >
                <g>
                  <path d="M11.4,93.3c-0.1,0-0.1,0-0.2,0c-1.3-0.1-2.2-1.2-2.1-2.4c1.3-19.1,19.3-34,40.9-34c21.6,0,39.6,14.9,40.9,33.9   c0.1,1.3-0.9,2.3-2.1,2.4c-1.3,0.1-2.3-0.9-2.4-2.1C85.2,74.4,69.2,61.4,50,61.4c-19.3,0-35.3,13.1-36.4,29.8   C13.5,92.4,12.5,93.3,11.4,93.3z" />
                  <path d="M50,52.3c-12.6,0-22.8-10.2-22.8-22.8S37.4,6.7,50,6.7c12.6,0,22.8,10.2,22.8,22.8S62.6,52.3,50,52.3z M50,11.3   c-10,0-18.2,8.2-18.2,18.2S40,47.7,50,47.7c10,0,18.2-8.2,18.2-18.2S60,11.3,50,11.3z" />
                </g>
              </svg>
            )}
          </div>
          <div className={classes.nameContainer}>
            <Typography fontSize={16}>
              {post.user.first_name + " " + post.user.last_name}
            </Typography>
            <Typography fontSize={12} className="postTime">
              {timeAgo.months > 0
                ? timeAgo.months + " months ago"
                : timeAgo.months === 12
                ? "1 year ago"
                : timeAgo.months > 12
                ? "More than a year ago"
                : timeAgo.days === 0
                ? "Today"
                : timeAgo.days === 1
                ? "Yesterday"
                : timeAgo.days + " days ago"}
            </Typography>
          </div>
        </Box>

        <Box className={classes.postContainer}>
          <Typography variant="h6" fontSize={18} fontWeight={500}>
            {post.title}
          </Typography>

          <Typography
            variant="body1"
            className={classes.postTextOverview}
            dangerouslySetInnerHTML={{ __html: post.content }}
          ></Typography>
          {post.imageURL && (
            <img className={classes.postImg} src={post.imageURL} alt="post" />
          )}
        </Box>

        <Box className={classes.likesContainer}>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                d="M7.48047 18.85L10.5805 21.25C10.9805 21.65 11.8805 21.85 12.4805 21.85H16.2805C17.4805 21.85 18.7805 20.95 19.0805 19.75L21.4805 12.45C21.9805 11.05 21.0805 9.84997 19.5805 9.84997H15.5805C14.9805 9.84997 14.4805 9.34997 14.5805 8.64997L15.0805 5.44997C15.2805 4.54997 14.6805 3.54997 13.7805 3.24997C12.9805 2.94997 11.9805 3.34997 11.5805 3.94997L7.48047 10.05"
                stroke="#9461FF"
                stroke-width="1.5"
                stroke-miterlimit="10"
              />
              <path
                d="M2.37891 18.85V9.05002C2.37891 7.65002 2.97891 7.15002 4.37891 7.15002H5.37891C6.77891 7.15002 7.37891 7.65002 7.37891 9.05002V18.85C7.37891 20.25 6.77891 20.75 5.37891 20.75H4.37891C2.97891 20.75 2.37891 20.25 2.37891 18.85Z"
                stroke="#9461FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Typography fontSize={14} fontWeight={500}>
              {post.likeCount} Likes
            </Typography>
          </div>

          <div>
            <Typography fontSize={14} fontWeight={500}>
              {post.replyCount} Comments
            </Typography>
          </div>
        </Box>

        <div className={classes.line}></div>

        <Box className={classes.optionsContainer}>
          <div className={likedByUser && classes.likedByUser}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                d="M7.48047 18.85L10.5805 21.25C10.9805 21.65 11.8805 21.85 12.4805 21.85H16.2805C17.4805 21.85 18.7805 20.95 19.0805 19.75L21.4805 12.45C21.9805 11.05 21.0805 9.84997 19.5805 9.84997H15.5805C14.9805 9.84997 14.4805 9.34997 14.5805 8.64997L15.0805 5.44997C15.2805 4.54997 14.6805 3.54997 13.7805 3.24997C12.9805 2.94997 11.9805 3.34997 11.5805 3.94997L7.48047 10.05"
                stroke-width="1.5"
                stroke-miterlimit="10"
              />
              <path
                d="M2.37891 18.85V9.05002C2.37891 7.65002 2.97891 7.15002 4.37891 7.15002H5.37891C6.77891 7.15002 7.37891 7.65002 7.37891 9.05002V18.85C7.37891 20.25 6.77891 20.75 5.37891 20.75H4.37891C2.97891 20.75 2.37891 20.25 2.37891 18.85Z"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Typography fontSize={14} fontWeight={500}>
              {likedByUser ? "Liked" : "Like"}
            </Typography>
          </div>

          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
            >
              <path
                d="M8.5 19.5H8C4 19.5 2 18.5 2 13.5V8.5C2 4.5 4 2.5 8 2.5H16C20 2.5 22 4.5 22 8.5V13.5C22 17.5 20 19.5 16 19.5H15.5C15.19 19.5 14.89 19.65 14.7 19.9L13.2 21.9C12.54 22.78 11.46 22.78 10.8 21.9L9.3 19.9C9.14 19.68 8.77 19.5 8.5 19.5Z"
                stroke="#64748B"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 8.5H17"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 13.5H13"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Typography fontSize={12} fontWeight={500}>
              Comment
            </Typography>
          </div>
        </Box>
        {/* <Box className={classes.nameBox}>
          <Typography fontWeight={600}>
            {post.user.first_name + " " + post.user.last_name}
          </Typography>
        </Box>

        <Box className={classes.postBox}>
          <div>
            <Typography variant="h6" fontWeight={600}>
              {post.title}
              <span className={classes.smallText}>
                (
                {timeAgo.months > 0
                  ? timeAgo.months + "months ago"
                  : timeAgo.months === 12
                  ? "1 year ago"
                  : timeAgo.months > 12
                  ? "More than a year ago"
                  : timeAgo.days === 0
                  ? "Today"
                  : timeAgo.days === 1
                  ? "Yesterday"
                  : timeAgo.days + " days ago"}
                )
              </span>
            </Typography>
          </div>

          <Typography
            variant="body1"
            className={classes.postTextOverview}
            dangerouslySetInnerHTML={{ __html: post.content }}
          ></Typography>
          {post.imageURL && (
            <img className={classes.postImg} src={post.imageURL} alt="post" />
          )}
        </Box>

        <Box className={classes.repliesBox}>
          <Typography
            className={classes.smallText}
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <ForumOutlinedIcon /> {post.replyCount} Replies
          </Typography>

          <Typography
            className={classes.smallText}
            sx={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            {likedByUser ? (
              <ThumbUpIcon
                sx={{
                  cursor: "pointer",
                  color: "#9461FF",
                }}
              />
            ) : (
              <ThumbUpOutlinedIcon
                sx={{
                  cursor: "pointer",
                }}
              />
            )}
            {post.likeCount} Likes
          </Typography>
        </Box> */}
      </Box>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(PostOverView);
